import { loadScript } from '@cp/utils/browser/script';
import { cleanObject, getProp, size } from '@cp/utils/object';
import { ISemanticEvent, SemanticContext, createPageViewEvent } from '../semanticEvents';
import { AsyncIntegration } from '../asyncIntegration';
import { Integration } from '../integration';

/**
 * YandexMetrika counter options
 * @url https://yandex.ru/support/metrica/code/counter-initialize.html
 */
export type YandexMetrikaCounterParams = {
  /**
   * Точный показатель отказов.
   * @default true
   */
  accurateTrackBounce?: boolean | number;
  /**
   * Признак записи содержимого iframe без счетчика в дочернем окне
   * @default false
   */
  childIframe?: boolean;
  /**
   * Признак сбора данных для карты кликов
   * @default true
   */
  clickmap?: boolean;
  /**
   * Признак отключения автоматической отправки данных при инициализации счетчика
   * @default false
   */
  defer?: boolean;
  /**
   * Сбор данных электронной коммерции.
   * @default false
   */
  ecommerce?: boolean | string | Array<any>;
  /**
   * Параметры визита, передаваемые во время инициализации счетчика.
   */
  params?: Array<any> | Record<string, any>;
  /**
   * Параметры посетителей сайта, передаваемые во время инициализации счетчика.
   */
  userParams?: Record<string, any>;
  /**
   * Признак отслеживания хеша в адресной строке браузера
   * @default false
   */
  trackHash?: boolean;
  /**
   * Признак отслеживания переходов по внешним ссылкам
   * @default true
   */
  trackLinks?: boolean;
  /**
   * Тип счетчика. Для РСЯ равен 1
   * @default 0
   */
  type?: number;
  /**
   * Признак использования Вебвизора
   * @default false
   */
  webvisor?: boolean;

  /**
   * Признак проверки готовности счетчика
   * @default false
   */
  triggerEvent?: boolean;
};

export type YandexMetrikaOptions = {
  /**
   * ID счетчика
   */
  counterId: string;
  /**
   * Признак передачи внутреннего ID пользователя
   * @default true
   */
  sendUserId: boolean;
  counterParams: YandexMetrikaCounterParams;
  purchaseGoalId?: string;
  /**
   * Соответствие имени SemanticEvent к имени цели в метрике
   */
  goals?: Record<string, string>;
  visitParamsVars?: Record<string, string>;
  userParamsVars?: Record<string, string>;
  dataLayerName?: string;
  debug?: boolean;
};

const DEFAULT_OPTIONS = {
  sendUserId: true,
  purchaseGoalId: undefined,
  counterParams: {},
  goals: {},
  visitParamsVars: {},
  userParamsVars: {},
  dataLayerName: 'dataLayer',
};
type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export class YandexMetrika extends AsyncIntegration implements Integration {
  protected options: YandexMetrikaOptions & typeof DEFAULT_OPTIONS;
  protected counterParams: YandexMetrikaCounterParams;
  protected dataLayerName: string;
  protected dataLayer: any[] = [];
  protected ym: any;
  // флаг показывает учитывать ли первый PaveView
  protected hit_allowed: boolean;

  constructor(
    options: Optional<YandexMetrikaOptions, keyof typeof DEFAULT_OPTIONS>,
    readonly global: any,
    ctx: SemanticContext,
  ) {
    super('YandexMetrika');
    this.options = Object.assign({}, DEFAULT_OPTIONS, options);
    this.dataLayerName = this.options.dataLayerName || DEFAULT_OPTIONS.dataLayerName;
    this.counterParams = this.options.counterParams;
    if (this.options.dataLayerName) {
      this.counterParams.ecommerce = this.options.dataLayerName;
    }

    this.hit_allowed = Boolean(this.counterParams.defer);

    this.init(ctx);
  }

  /**
   * Отправляет параметры визита
   * @url https://yandex.ru/support/metrica/data/visit-params_data.html
   * @param params
   * @private
   */
  private setVisitParams(params: Record<string, unknown>) {
    this.yaCounterCall('params', params);
  }

  /**
   * Отправляет параметры поселителя
   * @url https://yandex.ru/support/metrica/objects/user-params.html
   * @param params
   * @private
   */
  private setUserParams(params: Record<string, unknown>) {
    this.yaCounterCall('userParams', params);
  }

  private yaCounterCall(method: string, ...args: any) {
    this.ym(this.options.counterId, method, ...args);
  }

  private yaCounterInit(params?: Record<string, any>, userParams?: Record<string, any>) {
    this.ym(
      this.options.counterId,
      'init',
      Object.assign({}, this.counterParams, {
        params,
        userParams,
      }),
    );
  }

  init(ctx?: SemanticContext) {
    const window = this.global;
    window._ym_debug = this.options.debug;
    const ym =
      window.ym ||
      function() {
        // eslint-disable-next-line prefer-rest-params
        (ym.a = ym.a || []).push(arguments);
      };
    ym.l = Date.now();

    this.ym = window.ym = ym;

    this.dataLayer = window[this.dataLayerName] = window[this.dataLayerName] || [];

    return this.load()
      .then(() => {
        if (ctx) {
          //get id from metrika
          this.ym(this.options.counterId, 'getClientID', (id: string) => {
            ctx.client.yandexClientId = id;
          });
          if (ctx.client.client_id) {
            // pass our id to metrika
            this.ym(this.options.counterId, 'setUserID', String(ctx.client.client_id));
          }
        }

        this.yaCounterInit();
      })
      .then(this.onReady.bind(this))
      .catch(this.onInitFail.bind(this));
  }

  _trackEvent(event: ISemanticEvent, ctx: SemanticContext) {
    switch (event.name) {
      case createPageViewEvent.eventName:
        this.onViewedPage(event as ReturnType<typeof createPageViewEvent>, ctx);
        break;
      default:
        this.trackCustomEvent(event);
    }
  }

  async load() {
    const lib = 'https://mc.yandex.ru/metrika/tag.js';

    await loadScript(lib);
  }

  onViewedPage(event: ReturnType<typeof createPageViewEvent>, ctx: SemanticContext) {
    const { url, title, referrer } = event.payload;

    const userParams = cleanObject(this.getUserParams(ctx), true);

    if (size(userParams)) {
      this.setUserParams(userParams);
    }

    if (!this.hit_allowed) {
      // eslint-disable-next-line no-unused-expressions
      this.hit_allowed = true;
      return;
    }

    this.yaCounterCall('hit', url, { title, referrer });

    // send userId
    if (this.options.sendUserId) {
      const userId = getProp(event.payload, 'user.id');
      if (userId) {
        this.yaCounterCall('setUserID', userId);
      }
    }
  }

  trackCustomEvent(event: ISemanticEvent) {
    if (!event.goalId) {
      return;
    }
    const visitParams = cleanObject(this.getVisitParams(event), true);
    if (size(visitParams)) {
      this.yaCounterCall('reachGoal', event.goalId, visitParams);
    } else {
      this.yaCounterCall('reachGoal', event.goalId);
    }
  }

  private getUserParams(ctx: SemanticContext) {
    return {
      client_id: ctx.client.client_id,
      client_type: ctx.client.client_type,
      // https://yandex.ru/support/metrica/data/user-params-data.html#online
      UserID: ctx.client.client_id,
    };
  }

  private getVisitParams(event: ISemanticEvent): Record<string, any> {
    if (!event.payload) {
      return {};
    } else if (!event.goalName && event.goalId) {
      return event.payload;
    }
    const url = new URL(window.location.href);
    const name = event.goalName || event.goalId || event.name;
    return { [url.pathname]: { [name]: event.payload } };
  }
}
